/**
 * Pricing admin PURE guards — B2.5a' (fullsuite plan §B2.5a').
 *
 * Every guard is a total function over the dataset + the proposed row: it
 * returns a `PricingAdminError` or `null` and mutates NOTHING. The composed
 * write commands (`commands.ts`) are what callers use; the raw guards are
 * exported so the vitest matrices can assert each rejection independently and
 * so a UI can pre-validate without attempting a save.
 *
 * Grounding (never speculative breadth — every rule cites its source):
 * - HALF-OPEN bands `[minInclusive, maxExclusive)` — S1.5 / 07-18 B2.5 (OQ-L1,
 *   the $20 C/D overlap resolution). The overlap guard is the WRITE-side twin
 *   of `resolvePriceGroup`'s READ-side lookup: if two bands could both contain
 *   one pricing base, the read would be order-dependent, so the write refuses.
 * - Price levels are UNLIMITED user-defined rows — never the NetSuite PL01–17
 *   hardcode (07-18 B2.5 verbatim). Uniqueness is on the user-facing `code`,
 *   not on a fixed enum.
 * - Special item prices: mandatory reason + validity window + BOTH variants
 *   (fixed | markup_pct — the legacy 175% case is the markup variant).
 * - Purchase Cost is READ-ONLY on every surface (S9.1): `CostRecordPatch` has
 *   no member for it, and this guard refuses a smuggled one at run time too —
 *   the MSW/GraphQL layers take untyped JSON, so the type-level omission alone
 *   is not a guarantee there.
 * - Quantity schedule XOR ad-hoc brackets (DD!116) mirrors the ENGINE
 *   invariant in `quantityPricing.getEffectiveQuantityBrackets`, which throws
 *   on the same violation — the write must reject what the read cannot serve.
 * - Fixed-price immutability (Discribe-21, K126 = Q-S2): a locked row's price
 *   cannot move; unlocking is its own audited save.
 */

import type {
  CostRecord,
  CurrencyPriceRow,
  CustomerGroupDiscount,
  MarkupRule,
  PriceGroupBand,
  PriceLevel,
  PricingDataset,
  PricingGroup,
  ProductPricingFields,
  QuantityBracket,
  QuantityPricingSchedule,
  SpecialItemPrice,
} from '@/types/api/pricing';
import { PricingAdminError, type PricingAdminErrorCode } from './errors';

/** Editable cost-basis inputs. Purchase Cost is deliberately ABSENT — it is
 * read-only on every surface (S9.1), so an override is unrepresentable. */
export interface CostRecordPatch {
  landedAllocation?: number;
  internalMarkupPct?: number;
  salesMarkupPct?: number;
  basis?: CostRecord['basis'];
  effectiveDate?: string;
  /** S3.1 Y-S0 landed-cost opt-in (DD!39 追蹤到岸成本) — data only. */
  trackLandedCost?: boolean;
  /** S3.1 allocation-basis metadata (data only, no allocation math ships). */
  landedCostAllocationBasis?: CostRecord['landedCostAllocationBasis'];
}

/** One Group×Source×Level markup cell edit; `markupPct: null` removes it. */
export interface MarkupRuleEdit {
  priceGroupId: string;
  source: MarkupRule['source'];
  priceLevelId: string;
  markupPct: number | null;
}

const err = (code: PricingAdminErrorCode, field: string | null = null): PricingAdminError =>
  new PricingAdminError(code, field);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** ISO `YYYY-MM-DD`; the corpus is date-only throughout (no time components). */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Reason gate (every price-affecting save)
// ---------------------------------------------------------------------------

/**
 * A price-affecting save REQUIRES a business reason — never silently
 * defaulted (S9.1 anti-175%: the ReasonRequiredModal is the only entry, and
 * the accepted reason lands on the PRICE_OVERRIDE audit row).
 */
export function guardReason(reason: string | null | undefined): PricingAdminError | null {
  return reason && reason.trim().length > 0 ? null : err('REASON_REQUIRED', 'input.reason');
}

// ---------------------------------------------------------------------------
// Price levels
// ---------------------------------------------------------------------------

/** Level rows are unlimited; the USER-FACING `code` is the unique key. */
export function guardPriceLevelSave(
  levels: readonly PriceLevel[],
  level: PriceLevel,
): PricingAdminError | null {
  if (!level.id.trim()) return err('VALIDATION', 'input.id');
  const code = level.code.trim();
  if (!code) return err('VALIDATION', 'input.code');
  if (!level.nameEn.trim() || !level.nameZh.trim()) return err('VALIDATION', 'input.name');
  const clash = levels.some(
    (candidate) =>
      candidate.id !== level.id && candidate.code.trim().toLowerCase() === code.toLowerCase(),
  );
  return clash ? err('LEVEL_CODE_TAKEN', 'input.code') : null;
}

// ---------------------------------------------------------------------------
// Price group bands — HALF-OPEN geometry
// ---------------------------------------------------------------------------

/** Do two half-open bands `[min, max)` share at least one value? */
export function bandsOverlap(a: PriceGroupBand, b: PriceGroupBand): boolean {
  const aMax = a.maxExclusive ?? Number.POSITIVE_INFINITY;
  const bMax = b.maxExclusive ?? Number.POSITIVE_INFINITY;
  return a.minInclusive < bMax && b.minInclusive < aMax;
}

/**
 * Band write guard. A band must be a non-empty half-open interval, and it may
 * not overlap any OTHER band — an overlap makes `resolvePriceGroup` (which
 * returns the FIRST match) order-dependent, which is exactly the legacy $20
 * C/D defect this geometry was chosen to kill.
 */
export function guardPriceGroupBandSave(
  bands: readonly PriceGroupBand[],
  band: PriceGroupBand,
): PricingAdminError | null {
  if (!band.id.trim()) return err('VALIDATION', 'input.id');
  if (!isFiniteNumber(band.minInclusive) || band.minInclusive < 0) {
    return err('BAND_RANGE_INVALID', 'input.minInclusive');
  }
  if (band.maxExclusive !== null) {
    if (!isFiniteNumber(band.maxExclusive) || band.maxExclusive <= band.minInclusive) {
      return err('BAND_RANGE_INVALID', 'input.maxExclusive');
    }
  }
  if (!band.currency.trim()) return err('VALIDATION', 'input.currency');
  const clash = bands.some(
    (candidate) => candidate.id !== band.id && bandsOverlap(candidate, band),
  );
  return clash ? err('BAND_OVERLAP', 'input.minInclusive') : null;
}

// ---------------------------------------------------------------------------
// Markup rules (the Group × Source × Level matrix)
// ---------------------------------------------------------------------------

/**
 * A markup edit addresses one matrix CELL. Both referenced rows must exist
 * (a rule pointing at a deleted band or level would silently never match at
 * read time), and the percent is an increment ≥ −100 (−100 = free; below that
 * is a negative price).
 */
export function guardMarkupRuleEdit(
  dataset: Pick<PricingDataset, 'priceGroupBands' | 'priceLevels'>,
  edit: MarkupRuleEdit,
): PricingAdminError | null {
  if (!dataset.priceGroupBands.some((band) => band.id === edit.priceGroupId)) {
    return err('GROUP_NOT_FOUND', 'input.priceGroupId');
  }
  if (!dataset.priceLevels.some((level) => level.id === edit.priceLevelId)) {
    return err('LEVEL_NOT_FOUND', 'input.priceLevelId');
  }
  if (edit.markupPct === null) return null; // removal — nothing else to check
  if (!isFiniteNumber(edit.markupPct) || edit.markupPct < -100) {
    return err('PERCENT_RANGE', 'input.markupPct');
  }
  return null;
}

// ---------------------------------------------------------------------------
// Special item prices (BOTH variants; mandatory reason; validity window)
// ---------------------------------------------------------------------------

export function guardSpecialPriceSave(row: SpecialItemPrice): PricingAdminError | null {
  if (!row.id.trim()) return err('VALIDATION', 'input.id');
  if (!isFiniteNumber(row.customerId)) return err('VALIDATION', 'input.customerId');
  if (!row.productId.trim()) return err('VALIDATION', 'input.productId');
  if (!ISO_DATE.test(row.validFrom)) return err('VALIDITY_WINDOW_INVALID', 'input.validFrom');
  if (row.validTo !== null) {
    if (!ISO_DATE.test(row.validTo)) return err('VALIDITY_WINDOW_INVALID', 'input.validTo');
    if (row.validTo < row.validFrom) return err('VALIDITY_WINDOW_INVALID', 'input.validTo');
  }
  if (row.kind === 'fixed') {
    if (!isFiniteNumber(row.fixedPrice) || row.fixedPrice < 0) {
      return err('PRICE_RANGE', 'input.fixedPrice');
    }
    if (!row.currency.trim()) return err('VALIDATION', 'input.currency');
    return null;
  }
  // markup_pct — percent OF the pricing base (175 = 175% of base), so it is a
  // multiplier, not an increment: it must be positive.
  if (!isFiniteNumber(row.markupPct) || row.markupPct <= 0) {
    return err('PERCENT_RANGE', 'input.markupPct');
  }
  return null;
}

// ---------------------------------------------------------------------------
// Customer pricing assignment
// ---------------------------------------------------------------------------

export function guardCustomerPricingSave(
  dataset: Pick<PricingDataset, 'priceLevels'>,
  row: { customerId: number; priceLevelId: string | null; discountPct?: number },
): PricingAdminError | null {
  if (!isFiniteNumber(row.customerId)) return err('VALIDATION', 'input.customerId');
  if (row.priceLevelId !== null && !dataset.priceLevels.some((l) => l.id === row.priceLevelId)) {
    return err('LEVEL_NOT_FOUND', 'input.priceLevelId');
  }
  if (row.discountPct !== undefined) {
    if (!isFiniteNumber(row.discountPct) || row.discountPct < 0 || row.discountPct > 100) {
      return err('PERCENT_RANGE', 'input.discountPct');
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Cost records (Purchase Cost is READ-ONLY)
// ---------------------------------------------------------------------------

/**
 * `patch` is typed without `purchaseCost`, but the MSW/GraphQL layers receive
 * untyped JSON — so the smuggled key is refused at run time as well. The
 * refusal is explicit (`PURCHASE_COST_READONLY`), never a silent drop: a
 * caller that believed it edited the purchase cost must learn otherwise.
 */
export function guardCostRecordPatch(
  records: readonly CostRecord[],
  recordId: string,
  patch: CostRecordPatch & Record<string, unknown>,
): PricingAdminError | null {
  if (!records.some((record) => record.id === recordId)) {
    return err('RECORD_NOT_FOUND', 'input.recordId');
  }
  if ('purchaseCost' in patch) return err('PURCHASE_COST_READONLY', 'input.purchaseCost');
  if (patch.landedAllocation !== undefined) {
    if (!isFiniteNumber(patch.landedAllocation) || patch.landedAllocation < 0) {
      return err('PRICE_RANGE', 'input.landedAllocation');
    }
  }
  for (const key of ['internalMarkupPct', 'salesMarkupPct'] as const) {
    const value = patch[key];
    if (value === undefined) continue;
    if (!isFiniteNumber(value) || value < -100) return err('PERCENT_RANGE', `input.${key}`);
  }
  if (patch.effectiveDate !== undefined && !ISO_DATE.test(patch.effectiveDate)) {
    return err('VALIDATION', 'input.effectiveDate');
  }
  return null;
}

// ---------------------------------------------------------------------------
// Quantity pricing schedules (DD!116)
// ---------------------------------------------------------------------------

export function guardQuantityBrackets(
  brackets: readonly QuantityBracket[],
  field = 'input.brackets',
): PricingAdminError | null {
  if (brackets.length === 0) return err('BRACKETS_REQUIRED', field);
  const seen = new Set<number>();
  for (const bracket of brackets) {
    if (!isFiniteNumber(bracket.minQuantity) || bracket.minQuantity <= 0) {
      return err('VALIDATION', `${field}.minQuantity`);
    }
    if (seen.has(bracket.minQuantity)) return err('BRACKET_DUPLICATE', `${field}.minQuantity`);
    seen.add(bracket.minQuantity);
    // Blanket discount off the unit price: 0 is valid (a matched no-op
    // bracket), 100 would zero the price — both ends are inclusive bounds.
    if (!isFiniteNumber(bracket.discountPct) || bracket.discountPct < 0 || bracket.discountPct > 100) {
      return err('PERCENT_RANGE', `${field}.discountPct`);
    }
  }
  return null;
}

export function guardQuantityScheduleSave(
  schedules: readonly QuantityPricingSchedule[],
  schedule: QuantityPricingSchedule,
): PricingAdminError | null {
  if (!schedule.id.trim()) return err('VALIDATION', 'input.id');
  const code = schedule.code.trim();
  if (!code) return err('VALIDATION', 'input.code');
  const clash = schedules.some(
    (candidate) =>
      candidate.id !== schedule.id && candidate.code.trim().toLowerCase() === code.toLowerCase(),
  );
  if (clash) return err('SCHEDULE_CODE_TAKEN', 'input.code');
  return guardQuantityBrackets(schedule.brackets);
}

// ---------------------------------------------------------------------------
// Pricing groups + customer×group discount cards (Discribe-20)
// ---------------------------------------------------------------------------

export function guardPricingGroupSave(
  groups: readonly PricingGroup[],
  group: PricingGroup,
): PricingAdminError | null {
  if (!group.id.trim()) return err('VALIDATION', 'input.id');
  const code = group.code.trim();
  if (!code) return err('VALIDATION', 'input.code');
  if (!group.nameEn.trim() || !group.nameZh.trim()) return err('VALIDATION', 'input.name');
  const clash = groups.some(
    (candidate) =>
      candidate.id !== group.id && candidate.code.trim().toLowerCase() === code.toLowerCase(),
  );
  return clash ? err('GROUP_CODE_TAKEN', 'input.code') : null;
}

export function guardCustomerGroupDiscountSave(
  dataset: Pick<PricingDataset, 'priceLevels' | 'pricingGroups'>,
  cardRow: CustomerGroupDiscount,
): PricingAdminError | null {
  if (!cardRow.id.trim()) return err('VALIDATION', 'input.id');
  if (!isFiniteNumber(cardRow.customerId)) return err('VALIDATION', 'input.customerId');
  if (!(dataset.pricingGroups ?? []).some((group) => group.id === cardRow.groupId)) {
    return err('GROUP_NOT_FOUND', 'input.groupId');
  }
  if (cardRow.kind === 'discount_pct') {
    if (
      !isFiniteNumber(cardRow.discountPct) ||
      cardRow.discountPct < 0 ||
      cardRow.discountPct > 100
    ) {
      return err('PERCENT_RANGE', 'input.discountPct');
    }
    return null;
  }
  return dataset.priceLevels.some((level) => level.id === cardRow.priceLevelId)
    ? null
    : err('LEVEL_NOT_FOUND', 'input.priceLevelId');
}

// ---------------------------------------------------------------------------
// Manually-locked per-currency rows (Discribe-19/21)
// ---------------------------------------------------------------------------

/** The matrix CELL identity — (productId, currency, priceLevelId). */
export function isSameCurrencyCell(a: CurrencyPriceRow, b: CurrencyPriceRow): boolean {
  return (
    a.productId === b.productId &&
    a.currency === b.currency &&
    (a.priceLevelId ?? null) === (b.priceLevelId ?? null)
  );
}

/** The existing row occupying `priceRow`'s cell (by id OR by cell identity). */
export function findCurrencyCellRow(
  rows: readonly CurrencyPriceRow[],
  priceRow: CurrencyPriceRow,
): CurrencyPriceRow | undefined {
  return rows.find(
    (candidate) => candidate.id === priceRow.id || isSameCurrencyCell(candidate, priceRow),
  );
}

export function guardCurrencyPriceRowSave(
  dataset: Pick<PricingDataset, 'priceLevels' | 'currencyPriceRows'>,
  priceRow: CurrencyPriceRow,
): PricingAdminError | null {
  if (!priceRow.id.trim()) return err('VALIDATION', 'input.id');
  if (!priceRow.productId.trim()) return err('VALIDATION', 'input.productId');
  if (!priceRow.currency.trim()) return err('VALIDATION', 'input.currency');
  if (!isFiniteNumber(priceRow.price) || priceRow.price < 0) {
    return err('PRICE_RANGE', 'input.price');
  }
  if (
    priceRow.priceLevelId !== null &&
    !dataset.priceLevels.some((level) => level.id === priceRow.priceLevelId)
  ) {
    return err('LEVEL_NOT_FOUND', 'input.priceLevelId');
  }
  const existing = findCurrencyCellRow(dataset.currencyPriceRows ?? [], priceRow);
  // Discribe-21: while the cell is LOCKED its price is immutable. Unlocking
  // (fixedPrice off at the SAME price) is permitted — and is its own audited
  // save — so only a price CHANGE under an active lock is refused.
  if (existing && existing.fixedPrice && priceRow.price !== existing.price) {
    return err('FIXED_PRICE_LOCKED', 'input.price');
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per-product pricing fields (group / schedule / ad-hoc brackets / estimate)
// ---------------------------------------------------------------------------

export function guardProductPricingSave(
  dataset: Pick<PricingDataset, 'pricingGroups' | 'quantityPricingSchedules'>,
  fields: ProductPricingFields,
): PricingAdminError | null {
  if (!fields.productId.trim()) return err('VALIDATION', 'input.productId');
  const hasSchedule = fields.quantityScheduleId != null && fields.quantityScheduleId !== '';
  const hasAdHoc = (fields.quantityBrackets?.length ?? 0) > 0;
  // DD!116 either-or — mirrors the engine invariant; checked FIRST so the
  // caller learns the structural violation before any reference lookup.
  if (hasSchedule && hasAdHoc) return err('SCHEDULE_EITHER_OR', 'input.quantityScheduleId');
  if (
    fields.pricingGroupId != null &&
    !(dataset.pricingGroups ?? []).some((group) => group.id === fields.pricingGroupId)
  ) {
    return err('GROUP_NOT_FOUND', 'input.pricingGroupId');
  }
  if (
    hasSchedule &&
    !(dataset.quantityPricingSchedules ?? []).some(
      (schedule) => schedule.id === fields.quantityScheduleId,
    )
  ) {
    return err('RULE_NOT_FOUND', 'input.quantityScheduleId');
  }
  if (hasAdHoc) {
    return guardQuantityBrackets(
      fields.quantityBrackets as QuantityBracket[],
      'input.quantityBrackets',
    );
  }
  return null;
}
